// RegisterBusinessForm.jsx

import React, { useEffect, useRef, useState } from 'react';
import EmojiPicker from 'emoji-picker-react';
import imageCompression from "browser-image-compression";
import { navigate, safeFetch } from "./App";
import { v4 as uuidv4 } from 'uuid';
import { LocationPicker } from "./LocationPicker";
import { cacheSafeFetch, memoryCache } from "./cacheSafeFetch";
import Portal from './Portal';

import { PlusCircle, Upload, MapPin, CheckCircle, Globe, MessageCircle, Sparkles, Map, Zap, Target, Smartphone, ArrowLeft, AlertCircle, Search, Handshake, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import { Store } from 'lucide-react';
import NearbyZonesPopup from './components/NearbyZonesPopup';
import AiDescriptionDialog from './components/AiDescriptionDialog';

export default function RegisterBusinessForm({ onClose, chatId, initialType, initialZone, linkedBusinessId }) {
    // Overlay animation state
    const [showSubmittingOverlay, setShowSubmittingOverlay] = useState(false);
    const [showCheckmark, setShowCheckmark] = useState(false);
    const [type, setType] = useState(initialType || 'business');
    const entityLabel = type === 'event' ? 'מפגש' : type === 'barter' ? 'בארטר' : 'עסק';
    const [datetime, setDatetime] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [barterOffer, setBarterOffer] = useState(''); // What I give
    const [barterWant, setBarterWant] = useState(''); // What I want to receive
    const [phone, setPhone] = useState('');
    const [isWhatsApp, setIsWhatsApp] = useState(false);
    const [website, setWebsite] = useState('');
    const [images, setImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [zones, setZones] = useState([]);
    const [categories, setCategories] = useState([]);
    const [pendingZones, setPendingZones] = useState([]); // zones waiting for approval
    const [selectedZones, setSelectedZones] = useState(initialZone ? [initialZone] : []);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [submissionComplete, setSubmissionComplete] = useState(false);
    const [customZones, setCustomZones] = useState([]);
    const [customZoneInput, setCustomZoneInput] = useState('');
    const [customCategory, setCustomCategory] = useState('');
    const [zonesWithLocation, setZonesWithLocation] = useState([]); // array of zone objects with location
    const [activeDays, setActiveDays] = useState([0, 1, 2, 3, 4, 5, 6]); // 0=Sunday, 6=Saturday - default all days

    const telegramRef = useRef(null);
    const [submitting, setSubmitting] = useState(false);
    const [showEmojiPickerForName, setShowEmojiPickerForName] = useState(false);
    const [showEmojiPickerForDescription, setShowEmojiPickerForDescription] = useState(false);
    const [contactInfo, setContactInfo] = useState('');
    const [contactInfoError, setContactInfoError] = useState('');
    const [zoneSearch, setZoneSearch] = useState('');
    const [categorySearch, setCategorySearch] = useState('');

    const [activeTab, setActiveTab] = useState('details');
    const [showIntro, setShowIntro] = useState(true);
    const [location, setLocation] = useState({ lat: null, lng: null });
    const [locationSelected, setLocationSelected] = useState(false);
    const [address, setAddress] = useState('');
    const [wantsNewWebsite, setWantsNewWebsite] = useState(null);
    const tabsOrder = ['details', 'location', 'zones', 'categories', 'images'];

    // Founder Profile fields
    const [showFounderProfile, setShowFounderProfile] = useState(false);
    const [founderName, setFounderName] = useState('');
    const [founderAbout, setFounderAbout] = useState('');
    const [founderImage, setFounderImage] = useState(null);
    const [founderImagePreview, setFounderImagePreview] = useState(null);

    // For NearbyZonesPopup
    const [showNearbyZones, setShowNearbyZones] = useState(false);

    // For AI Description Dialog
    const [showAiDialog, setShowAiDialog] = useState(false);

    const validateEmail = (email) => {
        // Simple email regex
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const validateCurrentTab = () => {
        if (activeTab === 'details') {
            // For barter, validate barterOffer and barterWant instead of description
            if (type === 'barter') {
                if (!name.trim() || !barterOffer.trim() || !barterWant.trim() || !contactInfo.trim()) return false;
            } else {
                if (!name.trim() || !description.trim() || !contactInfo.trim()) return false;
            }
            if (!validateEmail(contactInfo)) return false;
            return true;
        }
        if (activeTab === 'zones') {
            const totalZones = selectedZones.length + customZones.length;
            return totalZones > 0 && totalZones <= 5;
        }
        if (activeTab === 'categories') {
            const totalCats = selectedCategories.length + (customCategory ? 1 : 0);
            return totalCats > 0 && totalCats <= 3;
        }
        // location and images are optional
        return true;
    };

    useEffect(() => {
        if (window.Telegram && window.Telegram.WebApp) {
            window.Telegram.WebApp.ready();
            window.Telegram.WebApp.expand();
            telegramRef.current = window.Telegram.WebApp;
        }

        const fetchMetadata = async () => {
            try {
                // Fetch categories and pending zones from zones-and-categories endpoint
                const categoriesRes = await cacheSafeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/zones-and-categories`, chatId, { method: 'GET' }, { cacheKey: `zones-and-categories-${chatId}` });
                const categoriesData = await categoriesRes.json();
                setCategories((categoriesData.categories || []).sort((a, b) => a.localeCompare(b, 'he')));
                setPendingZones(categoriesData.pending_zones || []);

                // Fetch zones with location data from user-locals endpoint
                const zonesRes = await cacheSafeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/user-locals?chat_id=${chatId}`, chatId, { method: 'GET' }, { cacheKey: `user-locals-${chatId}` });
                const zonesData = await zonesRes.json();

                // Extract zone names for the zone selection list
                const allZones = zonesData.all_zones || [];
                // Store full zone objects with both zone_id and zone_display_name
                const zoneObjects = allZones.map(z => ({
                    zone_id: z.zone_id,
                    zone_display_name: z.zone_display_name || z.zone_id
                })).sort((a, b) => (a.zone_display_name || a.zone_id).localeCompare(b.zone_display_name || b.zone_id, 'he'));
                setZones(zoneObjects);

                // Parse zones with location data for the map
                const zonesWithLoc = allZones
                    .filter(zone => zone.location)
                    .map(zone => {
                        try {
                            // Parse the location string
                            const locations = JSON.parse(zone.location);
                            if (Array.isArray(locations) && locations.length > 0) {
                                // Use the first location point
                                const loc = locations[0];
                                return {
                                    name: zone.zone_display_name || zone.zone_id,
                                    lat: loc.latitude,
                                    lng: loc.longitude
                                };
                            }
                        } catch (e) {
                            console.error(`Failed to parse location for zone ${zone.zone_id}:`, e);
                        }
                        return null;
                    })
                    .filter(Boolean);

                setZonesWithLocation(zonesWithLoc);
            } catch (err) {
                console.error("Failed to fetch metadata", err);
                setZonesWithLocation([]);
            }
        };

        fetchMetadata();
    }, []);

    const handleEmojiClickForName = (emojiData) => {
        setName((prev) => prev + emojiData.emoji);
        setShowEmojiPickerForName(false);
    };

    const handleEmojiClickForDescription = (emojiData) => {
        setDescription((prev) => prev + emojiData.emoji);
        setShowEmojiPickerForDescription(false);
    };


    const handleSubmit = async () => {
        // Don't show overlay for barter type
        if (type !== 'barter') {
            setShowSubmittingOverlay(true);
            setShowCheckmark(false);
        }
        setSubmitting(true);

        // Validate custom zones don't already exist in approved zones or pending zones
        for (const customZone of customZones) {
            const trimmedCustomZone = customZone.trim().toLowerCase();

            // Check if zone already exists in approved zones
            if (zones.some(z => (z.zone_display_name || z.zone_id).toLowerCase() === trimmedCustomZone)) {
                alert(`האזור "${customZone}" כבר קיים ברשימה. אנא בחר אותו מהרשימה במקום להוסיף אזור חדש.`);
                if (type !== 'barter') setShowSubmittingOverlay(false);
                setSubmitting(false);
                return;
            }

            // Check if zone is already waiting for approval
            if (pendingZones.some(z => z.toLowerCase() === trimmedCustomZone)) {
                alert(`האזור "${customZone}" כבר ממתין לאישור. אנא המתן לאישור או בחר אזור אחר.`);
                if (type !== 'barter') setShowSubmittingOverlay(false);
                setSubmitting(false);
                return;
            }
        }

        // Validate custom category doesn't already exist
        if (customCategory && categories.some(c => c.toLowerCase() === customCategory.trim().toLowerCase())) {
            alert(`הקטגוריה "${customCategory}" כבר קיימת ברשימה. אנא בחר אותה מהרשימה במקום להוסיף קטגוריה חדשה.`);
            if (type !== 'barter') setShowSubmittingOverlay(false);
            setSubmitting(false);
            return;
        }

        // Keep regular zones and custom zones separate
        const finalCategories = [...selectedCategories, ...(customCategory ? [customCategory.trim()] : [])];

        // Total zones validation (selected + custom)
        const totalZones = selectedZones.length + customZones.length;

        // For barter, combine the two fields into description
        const finalDescription = type === 'barter'
            ? `מה אני מציע: ${barterOffer}\n\nמה אני מחפש: ${barterWant}`
            : description;

        if (!name.trim() || !finalDescription.trim() || totalZones === 0 || finalCategories.length === 0 || !contactInfo.trim()) {
            alert("נא למלא את כל השדות המסומנים בכוכבית '*'");
            if (type !== 'barter') setShowSubmittingOverlay(false);
            setSubmitting(false);
            return;
        }

        const data = {
            type,
            datetime: type === 'event' ? datetime : null,
            name,
            description: finalDescription,
            phone,
            isWhatsApp,
            website,
            zones: selectedZones,  // ONLY regular zones, NOT custom zones
            categories: finalCategories,
            customZones: customZones.map(z => z.trim()),  // Custom zones sent separately
            contact_info: contactInfo,
            location: locationSelected ? JSON.stringify(location) : undefined,
            address: address.trim() || undefined,  // Optional address field
            wants_new_website: wantsNewWebsite === true,
            active_days: type === 'business' ? activeDays : undefined,  // Only for businesses
            linked_business_id: linkedBusinessId || undefined  // Link event to business if provided
        };

        let allFiles = [];

        // Process business/event images
        for (const file of images) {
            const isVideo = file.type.startsWith('video/');
            const baseName = file.name.replace(/_thumb|\.[^/.]+$/g, '');
            const uuid = uuidv4();

            if (isVideo) {
                const videoName = `${uuid}_${file.name}`;
                const renamedFile = new File([file], videoName, { type: file.type });
                allFiles.push({ file: renamedFile, name: videoName, type: file.type });
                continue;
            }

            const fullCompressed = await imageCompression(file, {
                maxSizeMB: 0.5,
                maxWidthOrHeight: 1280,
                useWebWorker: true,
                fileType: 'image/jpeg',
            });
            const fullName = `${uuid}_${baseName}.jpg`;
            const fullFile = new File([fullCompressed], fullName, { type: 'image/jpeg' });

            const thumbCompressed = await imageCompression(file, {
                maxSizeMB: 0.1,
                maxWidthOrHeight: 200,
                useWebWorker: true,
                fileType: 'image/jpeg',
            });
            const thumbName = `${uuid}_${baseName}_thumb.jpg`;
            const thumbFile = new File([thumbCompressed], thumbName, { type: 'image/jpeg' });

            allFiles.push(
                { file: fullFile, name: fullName, type: 'image/jpeg' },
                { file: thumbFile, name: thumbName, type: 'image/jpeg' }
            );
        }

        // Process founder profile image if provided
        const founderImageIndex = founderImage ? allFiles.length : -1;
        if (founderImage) {
            const baseName = founderImage.name.replace(/\.[^/.]+$/g, '');
            const uuid = uuidv4();
            const founderCompressed = await imageCompression(founderImage, {
                maxSizeMB: 0.3,
                maxWidthOrHeight: 400,
                useWebWorker: true,
                fileType: 'image/jpeg',
            });
            const founderFileName = `${uuid}_${baseName}_founder.jpg`;
            const founderFile = new File([founderCompressed], founderFileName, { type: 'image/jpeg' });
            allFiles.push({ file: founderFile, name: founderFileName, type: 'image/jpeg' });
        }

        const presignRes = await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/upload-urls', chatId, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                filenames: allFiles.map(f => f.name),
                content_types: allFiles.map(f => f.type),
                chat_id: chatId,
            }),
        });

        const { urls } = await presignRes.json();

        if (!urls || urls.length !== allFiles.length) {
            throw new Error('Mismatch in presigned URL count');
        }

        for (let i = 0; i < allFiles.length; i++) {
            const { file } = allFiles[i];
            const { url, fields } = urls[i];
            const formData = new FormData();
            Object.entries(fields).forEach(([key, val]) => formData.append(key, val));
            formData.append('file', file);
            try {
                const uploadRes = await fetch(url, {
                    method: 'POST',
                    body: formData,
                });
                if (!uploadRes.ok) {
                    const text = await uploadRes.text();
                    console.error(`Upload failed for ${file.name}:`, text);
                    if (type !== 'barter') setShowSubmittingOverlay(false);
                    setSubmitting(false);
                    throw new Error('Upload failed');
                }
            } catch (err) {
                console.error('Upload error:', err);
                if (type !== 'barter') setShowSubmittingOverlay(false);
                setSubmitting(false);
                throw err;
            }
        }

        // Extract founder image URL if it was uploaded
        let founderImageUrl = null;
        if (founderImageIndex >= 0 && urls[founderImageIndex]) {
            founderImageUrl = urls[founderImageIndex].fields.key;
        }

        // Add founder profile fields to data if provided
        const dataWithFounder = {
            ...data,
            ...(showFounderProfile && founderName ? { founder_name: founderName } : {}),
            ...(showFounderProfile && founderAbout ? { founder_about: founderAbout } : {}),
            ...(founderImageUrl ? { founder_image_url: founderImageUrl } : {}),
        };

        const createRes = await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/prod/business-details', chatId, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                chat_id: chatId,
                image_keys: urls.map(entry => entry.fields.key),
                data: dataWithFounder
            })
        });

        const createData = await createRes.json();
        const newBusinessId = createData.business_id;

        // Clear cache for all zones (both selected and custom)
        for (const zone of [...selectedZones, ...customZones])
            delete memoryCache[`businesses-type-${type}-${zone}-${chatId}`];
        setSubmissionComplete(true);
        setSubmitting(false);

        // For barter, redirect immediately without overlay animation
        if (type === 'barter') {
            if (newBusinessId) {
                navigate(`?entity=${newBusinessId}&welcome=true`);
            }
        } else {
            // Show checkmark, then fade out overlay for business/event
            setShowCheckmark(true);
            setTimeout(() => {
                setShowSubmittingOverlay(false);
                setShowCheckmark(false);
                // Redirect to business page with welcome flag
                if (newBusinessId) {
                    navigate(`?entity=${newBusinessId}&welcome=true`);
                }
            }, 1200);
        }
    };
    const filteredZones = zones.filter(z => (z.zone_display_name || z.zone_id).includes(zoneSearch));
    const filteredCategories = categories.filter(c => c.includes(categorySearch));

    if (chatId === 'null' || chatId === null || chatId === '' || chatId === 'guest') {
        return (
            <div className="fixed top-20 left-0 right-0 z-50 px-4">
                <div className="bg-warning-100 border border-warning-500 p-4 rounded-xl shadow-md text-black text-center space-y-3 max-w-md mx-auto">
                    <h2 className="text-lg font-bold flex items-center justify-center gap-2"><AlertCircle className="w-5 h-5" /> יש להתחבר <AlertCircle className="w-5 h-5" /></h2>
                    <p className="text-sm">
                        כדי לפרסם עסק או מפגש, נא להתחבר עם אחת מהאפשרויות:
                    </p>
                    <a
                        href="https://t.me/MekomitBot"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-info-600 text-white px-4 py-2 rounded-lg font-bold shadow hover:bg-info-700 transition"
                    >
                        כניסה דרך טלגרם
                    </a>
                </div>
            </div>
        );
    }

    const formContent = (
        <div className={onClose ? "w-full max-h-[90vh] overflow-y-auto bg-surface rounded-2xl shadow-2xl" : "min-h-screen overflow-y-auto bg-surface pb-24"}>
            {/* Full-screen submitting overlay with progress ring and checkmark */}
            <AnimatePresence>
                {showSubmittingOverlay && (
                    <motion.div
                        key="submitting-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm"
                        style={{ pointerEvents: 'all' }}
                    >
                        <div className="flex flex-col items-center justify-center">
                            <div className="relative flex items-center justify-center">
                                {/* Progress ring */}
                                {!showCheckmark && (
                                    <svg className="w-36 h-36 absolute animate-spin-slow" viewBox="0 0 120 120">
                                        <circle
                                            cx="60" cy="60" r="52"
                                            fill="none"
                                            stroke="#fbbf24"
                                            strokeWidth="8"
                                            strokeDasharray="326"
                                            strokeDashoffset="60"
                                            strokeLinecap="round"
                                            opacity="0.7"
                                        />
                                    </svg>
                                )}
                                {/* Entity icon or checkmark */}
                                <motion.div
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.8, opacity: 0 }}
                                    transition={{ duration: 0.4 }}
                                    className="flex items-center justify-center w-32 h-32 rounded-full bg-white shadow-xl"
                                    style={{ zIndex: 2 }}
                                >
                                    {showCheckmark ? (
                                        <motion.div
                                            initial={{ scale: 0.7, opacity: 0 }}
                                            animate={{ scale: 1.1, opacity: 1 }}
                                            exit={{ scale: 0.7, opacity: 0 }}
                                            transition={{ duration: 0.4 }}
                                        >
                                            <CheckCircle className="w-24 h-24 text-success-500 animate-pop" strokeWidth={2.5} />
                                        </motion.div>
                                    ) : (
                                        type === 'event' ? (
                                            <Sparkles className="w-24 h-24 text-warning-500 animate-fadeInUp" strokeWidth={2.5} />
                                        ) : type === 'barter' ? (
                                            <Handshake className="w-24 h-24 text-warning-500 animate-fadeInUp" strokeWidth={2.5} />
                                        ) : (
                                            <PlusCircle className="w-24 h-24 text-warning-500 animate-fadeInUp" strokeWidth={2.5} />
                                        )
                                    )}
                                </motion.div>
                            </div>
                            <div className="mt-8 text-2xl font-bold text-white drop-shadow-lg animate-fadeInUp">
                                {showCheckmark ? 'נוצר בהצלחה!' : `יוצר את ה${entityLabel} שלך...`}
                            </div>
                        </div>
                        <style>{`
                            @keyframes spin-slow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                            .animate-spin-slow { animation: spin-slow 2.2s linear infinite; }
                            @keyframes fadeInUp { 0% { opacity: 0; transform: translateY(24px); } 100% { opacity: 1; transform: translateY(0); } }
                            .animate-fadeInUp { animation: fadeInUp 1.2s cubic-bezier(0.4,0,0.2,1) both; }
                            @keyframes pop { 0% { transform: scale(0.7); opacity: 0; } 80% { transform: scale(1.15); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
                            .animate-pop { animation: pop 0.7s cubic-bezier(0.4,0,0.2,1) both; }
                        `}</style>
                    </motion.div>
                )}
            </AnimatePresence>
            {/* Guest warning */}
            {(chatId === 'null' || chatId === 'guest') && (
                <div className="bg-warning-100 text-ink-strong border border-warning-100 p-4 rounded-xl text-center shadow font-semibold max-w-lg mx-auto mt-8 relative">
                    ניתן להוסיף עסק \ מפגש רק כאשר נכנסים למקומית דרך{' '}
                    <a
                        href="https://t.me/MekomitBot"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-warning-700 font-bold"
                    >
                        טלגרם
                    </a>
                </div>
            )}

            {/* Intro Onboarding Card */}
            <AnimatePresence>
                {showIntro && chatId !== 'null' && !submissionComplete && (
                    <motion.div
                        key="intro-onboarding"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20, scale: 0.96 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="relative bg-gradient-to-br from-white via-warning-50/30 to-white rounded-[28px] mt-6 max-w-lg mx-auto px-6 py-7 space-y-5 border border-warning-100/60 overflow-hidden"
                        style={{
                            boxShadow: '0 20px 60px -12px rgba(251, 191, 36, 0.15), 0 0 1px rgba(0,0,0,0.05)',
                            backdropFilter: 'blur(20px)'
                        }}
                    >
                        {/* Decorative background elements */}
                        <div className="absolute top-0 right-0 w-56 h-56 bg-gradient-radial from-warning-100/20 to-transparent rounded-full blur-3xl -translate-y-28 translate-x-28" />
                        <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-radial from-warning-100/15 to-transparent rounded-full blur-2xl translate-y-20 -translate-x-20" />

                        {/* Content wrapper */}
                        <div className="relative z-10 space-y-4">
                            {/* Hero icon with animated gradient */}
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.2, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                className="flex justify-center mb-2"
                            >
                                <div className="relative">
                                    <div className="absolute inset-0 bg-gradient-to-br from-warning-100 to-warning-500 rounded-2xl blur-xl opacity-40 animate-pulse" />
                                    <div className="relative bg-gradient-to-br from-warning-500 via-warning-500 to-warning-600 p-3.5 rounded-2xl shadow-lg">
                                        {type === 'event' ? (
                                            <Sparkles className="w-8 h-8 text-white" strokeWidth={2.5} />
                                        ) : type === 'barter' ? (
                                            <Handshake className="w-8 h-8 text-white" strokeWidth={2.5} />
                                        ) : (
                                            <PlusCircle className="w-8 h-8 text-white" strokeWidth={2.5} />
                                        )}
                                    </div>
                                </div>
                            </motion.div>

                            {/* Title with improved typography */}
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.25, duration: 0.4 }}
                                className="text-center"
                            >
                                <h2 className="text-[24px] leading-tight font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-br from-ink-strong to-ink font-[Rubik, Heebo, sans-serif]">
                                    יצירת {entityLabel} במקומית
                                </h2>
                            </motion.div>

                            {/* Main description */}
                            <motion.p
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3, duration: 0.4 }}
                                className="text-[15px] text-ink font-[Rubik, Heebo, sans-serif] leading-[1.5] text-center px-1"
                            >
                                {type === 'barter'
                                    ? 'שתפו את השירותים והכישורים שלכם והתחברו לכלכלת החליפין המקומית'
                                    : `הרשמה קצרה ופשוטה כדי לעזור ל${entityLabel} שלך לגדול ולהתחבר לקהילה המקומית.`
                                }
                            </motion.p>

                            {/* Features list - more compact */}
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.35, duration: 0.4 }}
                                className="grid grid-cols-3 gap-2 py-1"
                            >
                                {[
                                    { Icon: Zap, text: '3 דקות' },
                                    { Icon: Target, text: 'חשיפה רחבה' },
                                    { Icon: Smartphone, text: 'קל ונוח' }
                                ].map((feature, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.4 + idx * 0.05, duration: 0.3 }}
                                        className="flex flex-col items-center gap-1 text-center"
                                    >
                                        <feature.Icon className="w-6 h-6 text-warning-500" strokeWidth={2} />
                                        <span className="text-[13px] font-medium text-ink leading-tight">{feature.text}</span>
                                    </motion.div>
                                ))}
                            </motion.div>

                            {/* Free website offer for businesses - more compact */}
                            {/* {type === 'business' && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.5, duration: 0.4 }}
                                    className="relative bg-gradient-to-br from-warning-50 to-warning-50 border-2 border-warning-100/60 rounded-xl p-3.5 shadow-sm overflow-hidden"
                                >
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-warning-100/20 rounded-full blur-2xl" />
                                    <div className="relative flex items-center gap-3">
                                        <div className="flex-shrink-0 bg-gradient-to-br from-warning-500 to-warning-500 p-2 rounded-lg shadow-md">
                                            <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-[13px] leading-snug text-ink-strong">
                                                <span className="font-bold text-ink-strong">בונוס!</span> נבנה לך אתר מקצועי <span className="font-bold text-warning-700">בחינם</span>
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            )} */}

                            {/* Online store feature for businesses */}
                            {type === 'business' && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.55, duration: 0.4 }}
                                    className="relative bg-gradient-to-br from-info-50 to-brand-50 border-2 border-info-100/60 rounded-xl p-3.5 shadow-sm overflow-hidden"
                                >
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-info-100/20 rounded-full blur-2xl" />
                                    <div className="relative flex items-center gap-3">
                                        <div className="flex-shrink-0 bg-gradient-to-br from-info-500 to-info-500 p-2 rounded-lg shadow-md">
                                            <Store className="w-4 h-4 text-white" strokeWidth={2.5} />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-[13px] leading-snug text-ink-strong">
                                                לאחר סיום הרישום, צרו <span className="font-bold text-info-700">חנות מקוונת בחינם </span> בקלות עם ניהול מלא של מוצרים והזמנות
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {/* CTA Buttons - more compact */}
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.55, duration: 0.4 }}
                                className="flex flex-col gap-2.5 pt-2"
                            >
                                <button
                                    type="button"
                                    onClick={() => setShowIntro(false)}
                                    className="group relative w-full bg-gradient-to-r from-warning-500 via-warning-500 to-warning-500 text-white py-3.5 rounded-xl font-bold text-[15px] shadow-lg shadow-warning-500/30 hover:shadow-xl hover:shadow-warning-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 overflow-hidden"
                                >
                                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
                                    <span className="relative flex items-center justify-center gap-2">
                                        <span>בואו נתחיל</span>
                                        <span className="text-lg">←</span>
                                    </span>
                                </button>

                                {/* {type === 'business' && (
                                    <a
                                        href="https://wa.me/972549980981?text=Hi!%20I%27d%20like%20to%20create%20a%20website%20for%20my%20business%20on%20Mekomit."
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group w-full bg-white border-2 border-success-500/30 text-success-700 py-3 rounded-xl font-semibold text-[14px] shadow-sm hover:shadow-md hover:border-success-500/50 hover:bg-success-50/50 transition-all duration-200 flex items-center justify-center gap-2"
                                    >
                                        <MessageCircle className="w-4 h-4 group-hover:scale-110 transition-transform" strokeWidth={2.2} />
                                        <span>צרו קשר לבניית אתר</span>
                                    </a>
                                )} */}
                            </motion.div>

                            {/* Footer note - smaller */}
                            {/* {type === 'business' && (
                                <motion.p
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.6, duration: 0.4 }}
                                    className="text-[12px] text-ink-subtle text-center pt-1"
                                >
                                    אין חובה באתר • החשיפה מתחילה מיד
                                </motion.p>
                            )} */}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main Form */}
            <AnimatePresence>
                {chatId !== 'null' && !submissionComplete && !showIntro && (
                    <motion.form
                        key="add-form"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 24 }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                        className="bg-surface text-ink-strong rounded-3xl shadow-xl mt-10 max-w-lg mx-auto px-4 py-8 space-y-6 relative"
                        style={{ boxShadow: '0 4px 32px 0 #e7e3d8' }}
                        onSubmit={e => { e.preventDefault(); }}
                    >


                        {/* Type Selector */}
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15 }}
                            className="flex gap-3 justify-center mb-4"
                        >
                            <button
                                type="button"
                                onClick={() => setType('business')}
                                className={`flex flex-col items-center gap-2 px-6 py-4 rounded-2xl font-semibold transition-all duration-200 ${type === 'business'
                                    ? 'bg-warning-500 text-ink-strong shadow-lg scale-105'
                                    : 'bg-white text-ink-muted shadow hover:shadow-md hover:scale-102'
                                    }`}
                            >
                                <Store className="w-6 h-6" strokeWidth={2.5} />
                                <span className="text-sm">עסק</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setType('event')}
                                className={`flex flex-col items-center gap-2 px-6 py-4 rounded-2xl font-semibold transition-all duration-200 ${type === 'event'
                                    ? 'bg-warning-500 text-ink-strong shadow-lg scale-105'
                                    : 'bg-white text-ink-muted shadow hover:shadow-md hover:scale-102'
                                    }`}
                            >
                                <Sparkles className="w-6 h-6" strokeWidth={2.5} />
                                <span className="text-sm">מפגש</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setType('barter')}
                                className={`flex flex-col items-center gap-2 px-6 py-4 rounded-2xl font-semibold transition-all duration-200 ${type === 'barter'
                                    ? 'bg-warning-500 text-ink-strong shadow-lg scale-105'
                                    : 'bg-white text-ink-muted shadow hover:shadow-md hover:scale-102'
                                    }`}
                            >
                                <Handshake className="w-6 h-6" strokeWidth={2.5} />
                                <span className="text-sm">בארטר</span>
                            </button>
                        </motion.div>

                        {/* Progress bar */}
                        <div className="bg-surface-sunken h-2 rounded-full mb-4">
                            <motion.div
                                className="bg-warning-500 h-2 rounded-full"
                                initial={{ width: 0 }}
                                animate={{ width: `${((tabsOrder.indexOf(activeTab) + 1) / tabsOrder.length) * 100}%` }}
                                transition={{ duration: 0.5 }}
                            />
                        </div>

                        {/* Website Offer Section removed (now handled in onboarding card) */}

                        {/* Details Section */}
                        <AnimatePresence mode="wait">
                            {activeTab === 'details' && (
                                <motion.div
                                    key="details"
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 16 }}
                                    transition={{ duration: 0.4 }}
                                    className="space-y-6"
                                >
                                    <div className="text-xs uppercase tracking-wide text-ink-subtle font-bold mb-1 font-[Rubik, Nunito, sans-serif]">פרטי ה{entityLabel}</div>
                                    {type === 'event' && (
                                        <div className="rounded-2xl bg-white shadow p-4 flex flex-col gap-2">
                                            <label className="text-sm font-semibold">תאריך ושעה של המפגש</label>
                                            <input type="datetime-local" value={datetime} onChange={(e) => setDatetime(e.target.value)} className="input-modern" />
                                        </div>
                                    )}
                                    <div className="rounded-2xl bg-white shadow p-4 flex flex-col gap-2 relative">
                                        <label className="text-sm font-semibold">
                                            {type === 'barter' ? 'כותרת הבארטר *' : `כותרת ${entityLabel} *`}
                                        </label>
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            className="input-modern pr-10"
                                            placeholder={type === 'barter' ? 'לדוגמה: שיעורי יוגה' : 'לדוגמה: יעל והלחם'}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowEmojiPickerForName((prev) => !prev)}
                                            className="absolute right-3 top-8 text-lg"
                                            title="הוסף אימוג'י"
                                        >
                                            😄
                                        </button>
                                        {showEmojiPickerForName && (
                                            <div className="absolute z-50 right-10 top-full -mt-10">
                                                <EmojiPicker onEmojiClick={handleEmojiClickForName} />
                                            </div>
                                        )}
                                        <div className="text-xs text-ink-subtle mt-1">
                                            {type === 'barter' ? 'תארו את השירות או המיומנות שאתם מציעים' : `הכניסו שם ברור ומזמין ל${entityLabel}`}
                                        </div>
                                    </div>
                                    {type === 'barter' ? (
                                        // Two separate fields for barter
                                        <>
                                            <div className="rounded-2xl bg-white shadow p-4 flex flex-col gap-2 relative">
                                                <label className="text-sm font-semibold">
                                                    מה אני מציע *
                                                </label>
                                                <textarea
                                                    value={barterOffer}
                                                    onChange={(e) => setBarterOffer(e.target.value)}
                                                    className="input-modern min-h-[72px]"
                                                    rows="3"
                                                    placeholder="תארו את השירות או המיומנות שאתם מציעים"
                                                />
                                                <div className="text-xs text-ink-subtle mt-1">
                                                    לדוגמה: שיעורי יוגה, שיפוצים, עיצוב גרפי
                                                </div>
                                            </div>
                                            <div className="rounded-2xl bg-white shadow p-4 flex flex-col gap-2 relative">
                                                <label className="text-sm font-semibold">
                                                    מה אני מחפש בתמורה *
                                                </label>
                                                <textarea
                                                    value={barterWant}
                                                    onChange={(e) => setBarterWant(e.target.value)}
                                                    className="input-modern min-h-[72px]"
                                                    rows="3"
                                                    placeholder="תארו מה אתם מחפשים... או תפתיעו אותי!"
                                                />
                                                <div className="text-xs text-ink-subtle mt-1">
                                                    ספרו מה תשמחו לקבל בתמורה (או כתבו "תפתיעו אותי" אם אתם פתוחים להצעות)
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        // Single description field for business/event
                                        <div className="rounded-2xl bg-white shadow p-4 flex flex-col gap-2 relative">
                                            <div className="flex items-center justify-between mb-1">
                                                <label className="text-sm font-semibold">
                                                    תיאור {entityLabel} *
                                                </label>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowAiDialog(true)}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-brand-50 to-secondary-50
                                                             text-brand-700 rounded-lg hover:from-brand-100 hover:to-secondary-100
                                                             transition-all duration-200 text-xs font-medium border border-brand-200"
                                                    title="עזרה מ-AI"
                                                >
                                                    <Sparkles className="w-3.5 h-3.5" />
                                                    <span>AI</span>
                                                </button>
                                            </div>
                                            <textarea
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                className="input-modern pr-10 min-h-[72px]"
                                                rows="3"
                                                placeholder={`ספר.י לחברי הקהילה על ה${entityLabel} שלך`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowEmojiPickerForDescription((prev) => !prev)}
                                                className="absolute left-3 top-16 text-lg"
                                                title="הוסף אימוג'י"
                                            >
                                                😄
                                            </button>
                                            {showEmojiPickerForDescription && (
                                                <div className="absolute z-50 mt-2 left-0">
                                                    <EmojiPicker onEmojiClick={handleEmojiClickForDescription} />
                                                </div>
                                            )}
                                            <div className="text-xs text-ink-subtle mt-1">
                                                ספרו בקצרה על ה{entityLabel}
                                            </div>
                                        </div>
                                    )}
                                    {type !== 'barter' && (
                                        <div className="rounded-2xl bg-white shadow p-4 flex flex-col gap-2">
                                            <label className="text-sm font-semibold">כתובת אתר {entityLabel} (אם יש)</label>
                                            <input
                                                type="url"
                                                value={website}
                                                onChange={(e) => setWebsite(e.target.value)}
                                                className="input-modern"
                                                placeholder="https://example.com"
                                            />
                                            <div className="text-xs text-ink-subtle mt-1">לא חובה — אם יש אתר ל{entityLabel}</div>
                                        </div>
                                    )}
                                    <div className="rounded-2xl bg-white shadow p-4 flex flex-col gap-2">
                                        <label className="text-sm font-semibold">מספר טלפון לתצוגה</label>
                                        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="input-modern mb-2" />
                                        <label className="flex items-center gap-2 text-xs">
                                            <input type="checkbox" checked={isWhatsApp} onChange={() => setIsWhatsApp(!isWhatsApp)} className="accent-warning-500" />
                                            המספר זמין ב־WhatsApp
                                        </label>
                                    </div>
                                    <div className="rounded-2xl bg-white shadow p-4 flex flex-col gap-2">
                                        <label className="text-sm font-semibold">אימייל ליצירת קשר *</label>
                                        <input
                                            type="email"
                                            value={contactInfo}
                                            onChange={(e) => {
                                                setContactInfo(e.target.value);
                                                if (!validateEmail(e.target.value)) {
                                                    setContactInfoError('נא להזין כתובת אימייל תקינה');
                                                } else {
                                                    setContactInfoError('');
                                                }
                                            }}
                                            className={`input-modern${contactInfoError ? ' border-danger-500' : ''}`}
                                            placeholder="your@email.com"
                                        />
                                        {contactInfoError && (
                                            <div className="text-xs text-danger-500 mt-1">{contactInfoError}</div>
                                        )}
                                        <div className="text-xs text-ink-subtle mt-1">פרטי קשר לאימות בלבד</div>
                                    </div>

                                    {/* Days of Week Selector - Only for business type */}
                                    {type === 'business' && (
                                        <div className="rounded-2xl bg-white shadow p-4 flex flex-col gap-3">
                                            <label className="text-sm font-semibold">ימי פעילות העסק</label>
                                            <div className="text-xs text-ink-subtle">סמנו את הימים בהם העסק פעיל</div>
                                            <div className="grid grid-cols-7 gap-2">
                                                {[
                                                    { day: 0, label: 'א', fullName: 'ראשון' },
                                                    { day: 1, label: 'ב', fullName: 'שני' },
                                                    { day: 2, label: 'ג', fullName: 'שלישי' },
                                                    { day: 3, label: 'ד', fullName: 'רביעי' },
                                                    { day: 4, label: 'ה', fullName: 'חמישי' },
                                                    { day: 5, label: 'ו', fullName: 'שישי' },
                                                    { day: 6, label: 'ש', fullName: 'שבת' }
                                                ].map((dayInfo) => {
                                                    const isActive = activeDays.includes(dayInfo.day);
                                                    return (
                                                        <button
                                                            key={dayInfo.day}
                                                            type="button"
                                                            onClick={() => {
                                                                if (isActive) {
                                                                    setActiveDays(activeDays.filter(d => d !== dayInfo.day));
                                                                } else {
                                                                    setActiveDays([...activeDays, dayInfo.day].sort());
                                                                }
                                                            }}
                                                            className={`py-2 px-1 rounded-lg font-semibold text-sm transition-all duration-200 ${isActive
                                                                ? 'bg-warning-500 text-white shadow-md scale-105'
                                                                : 'bg-surface-muted text-ink-subtle hover:bg-surface-sunken'
                                                                }`}
                                                            title={dayInfo.fullName}
                                                        >
                                                            {dayInfo.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <div className="text-xs text-ink-subtle mt-1">
                                                {activeDays.length === 7 ? 'פעיל כל השבוע' :
                                                    activeDays.length === 0 ? 'לא נבחרו ימים' :
                                                        `פעיל ${activeDays.length} ימים בשבוע`}
                                            </div>
                                        </div>
                                    )}

                                    {/* Founder Profile Section - Optional & Collapsible */}
                                    <div className="rounded-2xl bg-warning-50 border border-warning-100 shadow p-4 space-y-3">
                                        <button
                                            type="button"
                                            onClick={() => setShowFounderProfile(!showFounderProfile)}
                                            className="w-full flex items-center justify-between text-sm font-semibold text-warning-700 hover:text-warning-700 transition"
                                        >
                                            <span className="flex items-center gap-2">
                                                <Sparkles className="w-4 h-4" />
                                                פרופיל מייסד/ת (אופציונלי)
                                            </span>
                                            <span className="text-xl">{showFounderProfile ? '−' : '+'}</span>
                                        </button>
                                        <p className="text-xs text-warning-700">הוסיפו נגיעה אישית - שתפו את הסיפור שלכם!</p>

                                        <AnimatePresence>
                                            {showFounderProfile && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                    className="space-y-3 pt-2"
                                                >
                                                    <div>
                                                        <label className="text-sm font-semibold text-ink">שם מייסד/ת</label>
                                                        <input
                                                            type="text"
                                                            value={founderName}
                                                            onChange={(e) => setFounderName(e.target.value)}
                                                            className="input-modern mt-1"
                                                            placeholder="למשל: יעל כהן"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-semibold text-ink">אודות המייסד/ת</label>
                                                        <textarea
                                                            value={founderAbout}
                                                            onChange={(e) => setFounderAbout(e.target.value)}
                                                            className="input-modern mt-1 min-h-[60px]"
                                                            rows="2"
                                                            placeholder="ספרו בקצרה על עצמכם ועל המסע שהוביל ליצירת העסק..."
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-semibold text-ink">תמונת פרופיל מייסד/ת</label>
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            className="block w-full text-sm text-ink-strong bg-white rounded-xl border-0 p-2 cursor-pointer shadow-sm focus:outline-none focus:ring-2 focus:ring-warning-100 mt-1"
                                                            onChange={(e) => {
                                                                const file = e.target.files[0];
                                                                if (file) {
                                                                    setFounderImage(file);
                                                                    const reader = new FileReader();
                                                                    reader.onload = (event) => {
                                                                        setFounderImagePreview(event.target.result);
                                                                    };
                                                                    reader.readAsDataURL(file);
                                                                }
                                                            }}
                                                        />
                                                        {founderImagePreview && (
                                                            <div className="mt-2 flex justify-center">
                                                                <img
                                                                    src={founderImagePreview}
                                                                    alt="תצוגה מקדימה"
                                                                    className="w-24 h-24 rounded-full object-cover shadow-md border-2 border-warning-100"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Location Section */}
                        <AnimatePresence mode="wait">
                            {activeTab === 'location' && (
                                <motion.div
                                    key="location"
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 16 }}
                                    transition={{ duration: 0.4 }}
                                    className="space-y-4"
                                >
                                    <div className="text-xs uppercase tracking-wide text-ink-subtle font-bold mb-1 font-[Rubik, Nunito, sans-serif]">מיקום</div>
                                    <div className="rounded-2xl bg-white shadow p-4 flex flex-col gap-2">
                                        <label className="text-sm font-semibold flex items-center gap-2"><MapPin className="w-4 h-4 text-warning-500" /> מיקום פיזי  (לא חובה)</label>
                                        {!locationSelected ? (
                                            <p className="text-xs text-ink-subtle">טרם נבחר מיקום — בחר.י נקודה במפה כדי לשמור את מיקום ה{entityLabel}.</p>
                                        ) : (
                                            <p className="text-xs text-success-600">נבחר מיקום: {location.lat.toFixed(5)}, {location.lng.toFixed(5)} ✅</p>
                                        )}
                                        <div className="h-64 relative overflow-hidden rounded-xl mt-2">
                                            <LocationPicker
                                                initialLocation={location}
                                                onLocationSelect={({ lat, lng }) => {
                                                    setLocation({ lat, lng });
                                                    setLocationSelected(true);
                                                }}
                                            />
                                        </div>

                                        {/* Address text field */}
                                        <div className="pt-4 border-t border-line-subtle mt-4">
                                            <label className="text-sm font-semibold mb-2 block">כתובת (לא חובה)</label>
                                            <input
                                                type="text"
                                                value={address}
                                                onChange={(e) => setAddress(e.target.value)}
                                                className="input-modern w-full"
                                                placeholder="לדוגמה: רחוב הרצל 10, תל אביב"
                                            />
                                            <div className="text-xs text-ink-subtle mt-1">
                                                הזן כתובת מדויקת אם ברצונך להציג אותה למשתמשים
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Zones Section */}
                        <AnimatePresence mode="wait">
                            {activeTab === 'zones' && (
                                <motion.div
                                    key="zones"
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 16 }}
                                    transition={{ duration: 0.4 }}
                                    className="space-y-4"
                                >
                                    <div className="text-xs uppercase tracking-wide text-ink-subtle font-bold mb-1 font-[Rubik, Nunito, sans-serif]">אזורי פעילות {entityLabel}</div>
                                    <div className="rounded-2xl bg-white shadow p-4 flex flex-col gap-2">

                                        <label className="text-sm font-semibold">בחר.י עד חמש קהילות שבהן יופיע {entityLabel} *</label>
                                        <div className="text-xs font-bold text-danger-500 mb-2">ניתן לבחור עד 5 קהילות בלבד</div>
                                        <label className="text-base font-bold text-warning-700 mt-1">משתמשים שייכנסו למקומיות אלה ייראו את {entityLabel} שלך</label>
                                        <label className="text-sm font-semibold text-warning-600 mb-2">לדוגמא: היישוב שבו אתם פועלים ויישובים באזור</label>
                                        {/* Nearby zones helper button - moved above search and checkboxes */}
                                        <button
                                            type="button"
                                            className="flex items-center gap-2 self-start px-3 py-1.5 rounded-full text-sm font-semibold bg-warning-100 text-warning-700 border border-warning-100 shadow-sm hover:bg-warning-100 transition mb-2 mt-1"
                                            onClick={() => setShowNearbyZones(true)}
                                        >
                                            <Map className="w-4 h-4 text-warning-500" />
                                            הצג קהילות בקרבתי
                                        </button>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={zoneSearch}
                                                onChange={(e) => setZoneSearch(e.target.value)}
                                                placeholder="חפש אזור..."
                                                className="input-modern mb-2 pl-9"
                                            />
                                            <Search className="w-4 h-4 absolute left-3 top-3 text-ink-subtle" strokeWidth={2} />
                                        </div>
                                        <div className="grid grid-cols-2 gap-y-2 gap-x-4 mb-2">
                                            {filteredZones.map((zone) => {
                                                const isSelected = selectedZones.includes(zone.zone_id);
                                                const totalZones = isSelected
                                                    ? selectedZones.length + customZones.length - 1
                                                    : selectedZones.length + customZones.length;
                                                return (
                                                    <label key={zone.zone_id} className="flex items-center justify-between p-2 bg-surface-muted rounded-lg shadow-sm cursor-pointer transition hover:bg-warning-50">
                                                        <input type="checkbox" checked={isSelected} onChange={() => {
                                                            if (isSelected) {
                                                                setSelectedZones(selectedZones.filter((z) => z !== zone.zone_id));
                                                            } else if (totalZones < 5) {
                                                                setSelectedZones([...selectedZones, zone.zone_id]);
                                                            }
                                                        }} className="accent-warning-500 ml-2" />
                                                        <span className="text-sm">{zone.zone_display_name || zone.zone_id}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>

                                        {/* Custom zones section */}
                                        <div className="mt-4 pt-4 border-t border-line">
                                            <label className="text-sm font-semibold text-brand-700 mb-2 block">לא מוצא.ת קהילה מתאימה ?</label>

                                            <label className="text-xs font-semibold text-brand-700 mb-2 block">אזורים מותאמים אישית (בכפוף לאישור מנהל)</label>
                                            {/* Display custom zones as chips */}
                                            {customZones.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mb-3">
                                                    {customZones.map((customZone, index) => (
                                                        <div key={index} className="flex items-center gap-1.5 bg-brand-100 text-brand-700 px-3 py-1.5 rounded-full text-sm font-medium border border-brand-200">
                                                            <span>{customZone}</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => setCustomZones(customZones.filter((_, i) => i !== index))}
                                                                className="hover:bg-brand-200 rounded-full p-0.5 transition"
                                                                title="הסר אזור"
                                                            >
                                                                <span className="text-xs">✕</span>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Add custom zone input */}
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={customZoneInput}
                                                    onChange={e => setCustomZoneInput(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && customZoneInput.trim()) {
                                                            e.preventDefault();
                                                            const trimmedInput = customZoneInput.trim();
                                                            const lowerInput = trimmedInput.toLowerCase();
                                                            const totalZones = selectedZones.length + customZones.length;

                                                            // Check if zone exists in approved zones
                                                            if (zones.some(z => (z.zone_display_name || z.zone_id).toLowerCase() === lowerInput)) {
                                                                alert(`האזור "${trimmedInput}" כבר קיים ברשימה. אנא בחר אותו מהרשימה במקום להוסיף אזור חדש.`);
                                                                setCustomZoneInput('');
                                                                return;
                                                            }

                                                            // Check if zone is pending approval
                                                            if (pendingZones.some(z => z.toLowerCase() === lowerInput)) {
                                                                alert(`האזור "${trimmedInput}" כבר ממתין לאישור. אנא המתן לאישור או בחר אזור אחר.`);
                                                                setCustomZoneInput('');
                                                                return;
                                                            }

                                                            if (totalZones < 5 && !customZones.includes(trimmedInput)) {
                                                                setCustomZones([...customZones, trimmedInput]);
                                                                setCustomZoneInput('');
                                                            }
                                                        }
                                                    }}
                                                    placeholder={
                                                        selectedZones.length + customZones.length >= 5
                                                            ? 'הגעת למגבלת 5 קהילות'
                                                            : "שם אזור חדש"
                                                    }
                                                    className="input-modern flex-1"
                                                    maxLength={30}
                                                    disabled={selectedZones.length + customZones.length >= 5}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (customZoneInput.trim()) {
                                                            const trimmedInput = customZoneInput.trim();
                                                            const lowerInput = trimmedInput.toLowerCase();
                                                            const totalZones = selectedZones.length + customZones.length;

                                                            // Check if zone exists in approved zones
                                                            if (zones.some(z => (z.zone_display_name || z.zone_id).toLowerCase() === lowerInput)) {
                                                                alert(`האזור "${trimmedInput}" כבר קיים ברשימה. אנא בחר אותו מהרשימה במקום להוסיף אזור חדש.`);
                                                                setCustomZoneInput('');
                                                                return;
                                                            }

                                                            // Check if zone is pending approval
                                                            if (pendingZones.some(z => z.toLowerCase() === lowerInput)) {
                                                                alert(`האזור "${trimmedInput}" כבר ממתין לאישור. אנא המתן לאישור או בחר אזור אחר.`);
                                                                setCustomZoneInput('');
                                                                return;
                                                            }

                                                            if (totalZones < 5 && !customZones.includes(trimmedInput)) {
                                                                setCustomZones([...customZones, trimmedInput]);
                                                                setCustomZoneInput('');
                                                            }
                                                        }
                                                    }}
                                                    disabled={!customZoneInput.trim() || selectedZones.length + customZones.length >= 5}
                                                    className="px-4 py-2 bg-brand-600 text-white rounded-xl font-semibold shadow hover:bg-brand-700 disabled:bg-line disabled:cursor-not-allowed transition"
                                                >
                                                    + הוסף
                                                </button>
                                            </div>
                                            <p className="text-xs text-ink-subtle mt-2">
                                                סה"כ נבחרו: {selectedZones.length + customZones.length}/5 קהילות
                                            </p>
                                        </div>
                                    </div>
                                    {/* Popup for nearby zones */}
                                    <NearbyZonesPopup
                                        open={showNearbyZones}
                                        onClose={() => setShowNearbyZones(false)}
                                        zones={zonesWithLocation}
                                        selectedZones={selectedZones}
                                        onSelectZone={zoneName => {
                                            const totalZones = selectedZones.length + customZones.length;
                                            if (!selectedZones.includes(zoneName) && totalZones < 5) {
                                                setSelectedZones([...selectedZones, zoneName]);
                                            }
                                            setShowNearbyZones(false);
                                        }}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Categories Section */}
                        <AnimatePresence mode="wait">
                            {activeTab === 'categories' && (
                                <motion.div
                                    key="categories"
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 16 }}
                                    transition={{ duration: 0.4 }}
                                    className="space-y-4"
                                >
                                    <div className="text-xs uppercase tracking-wide text-ink-subtle font-bold mb-1 font-[Rubik, Nunito, sans-serif]">קטגוריות {entityLabel}</div>
                                    <div className="rounded-2xl bg-white shadow p-4 flex flex-col gap-2">
                                        <label className="text-sm font-semibold">בחר עד שלוש קטגוריות ל{entityLabel} *</label>
                                        <label className="text-base font-bold text-warning-700 mt-1">משתמשים יכולים לסנן את {entityLabelPlural()} בתצוגה על פי קטגוריות</label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={categorySearch}
                                                onChange={(e) => setCategorySearch(e.target.value)}
                                                placeholder="חפש קטגוריה..."
                                                className="input-modern mb-2 pl-9"
                                            />
                                            <Search className="w-4 h-4 absolute left-3 top-3 text-ink-subtle" strokeWidth={2} />
                                        </div>
                                        <div className="grid grid-cols-2 gap-y-2 gap-x-4 mb-2">
                                            {filteredCategories.map((cat) => {
                                                const totalCats = selectedCategories.includes(cat)
                                                    ? selectedCategories.length + (customCategory ? 1 : 0) - 1
                                                    : selectedCategories.length + (customCategory ? 1 : 0);
                                                return (
                                                    <label key={cat} className="flex items-center justify-between p-2 bg-surface-muted rounded-lg shadow-sm cursor-pointer transition hover:bg-warning-50">
                                                        <input type="checkbox" checked={selectedCategories.includes(cat)} onChange={() => {
                                                            if (selectedCategories.includes(cat)) {
                                                                setSelectedCategories(selectedCategories.filter((c) => c !== cat));
                                                            } else if (totalCats < 3) {
                                                                setSelectedCategories([...selectedCategories, cat]);
                                                            }
                                                        }} className="accent-warning-500 ml-2" />
                                                        <span className="text-sm">{cat}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        <label className="text-xs text-ink-subtle mt-2">או הוסיפו קטגוריה חדשה ל{entityLabel} (בכפוף לאישור מנהל)</label>
                                        <input
                                            type="text"
                                            value={customCategory}
                                            onChange={e => {
                                                const val = e.target.value;
                                                if (val && selectedCategories.length >= 3) return;
                                                setCustomCategory(val);
                                            }}
                                            placeholder={
                                                selectedCategories.length + (customCategory ? 1 : 0) >= 3 && !customCategory
                                                    ? 'הגעת למגבלת 3 קטגוריות'
                                                    : "קטגוריה חדשה ל" + entityLabel
                                            }
                                            className="input-modern"
                                            maxLength={30}
                                            disabled={selectedCategories.length + (customCategory ? 1 : 0) >= 3 && !customCategory}
                                        />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Images Section */}
                        <AnimatePresence mode="wait">
                            {activeTab === 'images' && (
                                <motion.div
                                    key="images"
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 16 }}
                                    transition={{ duration: 0.4 }}
                                    className="space-y-4"
                                >
                                    <div className="text-xs uppercase tracking-wide text-ink-subtle font-bold mb-1 font-[Rubik, Nunito, sans-serif]">מדיה ל{entityLabel}</div>
                                    <div className="rounded-2xl bg-white shadow p-4 flex flex-col gap-2">
                                        <label className="text-sm font-semibold flex items-center gap-2"><Upload className="w-4 h-4 text-warning-500" /> תמונות ל{entityLabel} (עד 5). לא חובה</label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            className="block w-full text-sm text-ink-strong bg-surface-muted rounded-xl border-0 p-2 cursor-pointer shadow-sm focus:outline-none focus:ring-2 focus:ring-warning-100"
                                            onChange={(e) => {
                                                const selectedFiles = Array.from(e.target.files).slice(0, 5);
                                                const validFiles = [];
                                                const previews = [];

                                                for (const file of selectedFiles) {
                                                    validFiles.push(file);
                                                }

                                                setImages(validFiles);

                                                validFiles.forEach((file, index) => {
                                                    const reader = new FileReader();
                                                    reader.onload = (event) => {
                                                        previews[index] = {
                                                            type: "image",
                                                            url: event.target.result,
                                                        };
                                                        if (previews.filter(Boolean).length === validFiles.length) {
                                                            setImagePreviews(previews);
                                                        }
                                                    };
                                                    reader.readAsDataURL(file);
                                                });
                                            }}
                                        />
                                        <div className="text-xs text-ink-subtle mt-1">העלו עד 5 תמונות</div>
                                        {images.length > 0 && (
                                            <p className="text-success-600 text-xs mt-2 font-bold flex items-center gap-1"><CheckCircle className="w-4 h-4 text-success-500" /> {images.length} קבצים נבחרו</p>
                                        )}
                                        {imagePreviews.length > 0 && (
                                            <div className="grid grid-cols-2 gap-2 mt-3">
                                                {imagePreviews.map((preview, idx) => (
                                                    preview.type === "image" ? (
                                                        <img
                                                            key={idx}
                                                            src={preview.url}
                                                            alt={`preview ${idx + 1}`}
                                                            className="w-full h-32 object-cover rounded-md shadow"
                                                        />
                                                    ) : (
                                                        <video
                                                            key={idx}
                                                            src={preview.url}
                                                            controls
                                                            className="w-full h-32 object-cover rounded-md shadow"
                                                        />
                                                    )
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Navigation Buttons */}
                        <div className="flex gap-2 mt-4">
                            {tabsOrder.indexOf(activeTab) > 0 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const currentIndex = tabsOrder.indexOf(activeTab);
                                        setActiveTab(tabsOrder[currentIndex - 1]);
                                    }}
                                    className="w-full bg-line text-ink-strong py-3 rounded-full font-semibold shadow hover:shadow-lg hover:-translate-y-[1px] transition-all duration-200"
                                >
                                    חזור
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    const currentIndex = tabsOrder.indexOf(activeTab);
                                    const isLastTab = currentIndex === tabsOrder.length - 1;

                                    if (!validateCurrentTab()) {
                                        alert('נא למלא את כל השדות החיוניים לפני מעבר הלאה');
                                        return;
                                    }

                                    if (isLastTab) {
                                        handleSubmit();
                                    } else {
                                        setActiveTab(tabsOrder[currentIndex + 1]);
                                    }
                                }}
                                className="w-full bg-warning-500 text-ink-strong py-3 rounded-full font-bold shadow hover:shadow-lg hover:-translate-y-[1px] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-warning-100 flex justify-center items-center gap-2"
                                disabled={submitting}
                            >
                                {submitting && tabsOrder.indexOf(activeTab) === tabsOrder.length - 1 ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-ink-strong" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        שולח...
                                    </>
                                ) : (
                                    tabsOrder.indexOf(activeTab) === tabsOrder.length - 1 ? 'שלח' : 'הבא'
                                )}
                            </button>
                        </div>

                    </motion.form>
                )}
            </AnimatePresence>

            {/* Submitting Spinner (replaced by overlay) */}

            {/* Success State */}
            <AnimatePresence>
                {submissionComplete && (
                    <motion.div
                        key="success"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.5 }}
                        className="bg-success-50 border border-success-100 text-success-700 p-8 rounded-3xl shadow-xl text-center space-y-4 mt-10 max-w-lg mx-auto relative"
                    >
                        <div className="flex justify-center mb-2">
                            <CheckCircle className="w-10 h-10 text-success-500" />
                        </div>
                        <h2 className="text-2xl font-extrabold font-[Rubik, Nunito, sans-serif]">נוסף בהצלחה!</h2>
                        <p className="text-sm">במידה ויהיה צורך בבירורים ניצור עמך קשר</p>
                        <button
                            onClick={() => navigate('zones')}
                            className="bg-warning-500 text-ink-strong font-bold px-8 py-3 rounded-full shadow hover:shadow-lg hover:-translate-y-[1px] transition-all duration-200 border-2 border-warning-100 flex items-center justify-center gap-2"
                        >
                            <ArrowLeft className="w-5 h-5" strokeWidth={2.5} />
                            חזרה למסך הראשי
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* AI Description Dialog */}
            {showAiDialog && (
                <AiDescriptionDialog
                    initialText={description}
                    entityType={type}
                    onApply={(newText) => setDescription(newText)}
                    onClose={() => setShowAiDialog(false)}
                    extraContext={{
                        name: name,
                        category: selectedCategories.join(', ')
                    }}
                    chatId={chatId}
                />
            )}

            {/* Modern input style */}
            <style>{`
                .input-modern {
                    background: #fff;
                    border: none;
                    border-radius: 1rem;
                    box-shadow: 0 1px 4px 0 #ece7de;
                    padding: 0.75rem 1rem;
                    font-family: 'Inter', 'Rubik', 'Nunito', sans-serif;
                    font-size: 1rem;
                    color: #222;
                    font-weight: 500;
                    outline: none;
                    transition: box-shadow 0.2s, background 0.2s;
                }
                .input-modern:focus {
                    background: #F4F2EF;
                    box-shadow: 0 2px 8px 0 #e7e3d8;
                }


            `}</style>
        </div>
    );

    if (onClose) {
        return (
            <Portal>
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000] flex items-center justify-center p-6 animate-fade-in">
                    <div className="relative w-full max-w-lg">
                        {/* Close button */}
                        <button
                            onClick={onClose}
                            className="absolute -top-12 right-0 text-white hover:text-ink-subtle transition-colors z-10"
                            aria-label="Close"
                        >
                            <X className="w-8 h-8" />
                        </button>
                        {formContent}
                    </div>
                </div>
            </Portal>
        );
    }

    return formContent;
}


// Helper for plural entity label
function entityLabelPlural(type) {
    return type === 'event' ? 'המפגשים' : 'העסקים';
}
